import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { useUpdateTotalExpectedPaymentAmountMutation } from "@services/bills/mutations";
import {
  type IBillData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import {
  formatDecimalNumberInputValue,
  parseDecimalNumberInputValue,
} from "@utils/units";

interface IBillTotalAmountFormProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  billId: IBillData["billId"];
  amount: number;
  onClose?: () => void;
}

interface IFormData {
  amount: string;
}

export const BillTotalAmountForm: React.FC<IBillTotalAmountFormProps> = ({
  jointBillServiceId,
  periodId,
  billId,
  amount,
  onClose,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const { mutateAsync: updateAmount, isPending } =
    useUpdateTotalExpectedPaymentAmountMutation({
      jointBillServiceId,
      periodId,
    });

  const { handleSubmit, control, setValue, setFocus } = useForm<IFormData>({
    defaultValues: {
      amount: formatDecimalNumberInputValue(amount, undefined),
    },
  });

  const parseAmountValue = (value: string) =>
    Math.abs(parseDecimalNumberInputValue(value));

  const reformatField = (value: string) => {
    setValue("amount", formatDecimalNumberInputValue(parseAmountValue(value)));
  };

  const changeAmount = async (data: IFormData) => {
    reformatField(data.amount);
    const amountValue = parseAmountValue(data.amount);
    const result = await updateAmount({
      totalExpectedPaymentAmount: amountValue,
      billId,
    }).catch(() => {
      onClose?.();
      toast.error(t("billTotalAmountForm.toast.error"));
    });

    if (!isNullOrUndefined(result)) {
      onClose?.();
      toast.success(t("billTotalAmountForm.toast.success"));
    }
  };

  useEffect(() => {
    setFocus("amount");
  }, [setFocus]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(changeAmount)}
      className="absolute inset-y-0 right-0 top-0 my-auto h-9 bg-white md:top-1/2 md:my-0 md:-translate-y-1/2 md:gap-4"
    >
      <div className="w-28">
        <FormInput
          name="amount"
          control={control}
          inputSize="small"
          onBlur={() => {
            formRef.current?.dispatchEvent(
              new Event("submit", { cancelable: true, bubbles: true }),
            );
          }}
          onFocus={() => {
            if (amount === 0) {
              setValue("amount", "");
            }
          }}
          inputMode="decimal"
          isLoading={isPending}
        />
      </div>
    </form>
  );
};
